import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { useAuthStore, User } from '../store/auth-store';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUpWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, login, logout, refreshToken } = useAuthStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated from persisted store
    setLoading(false);
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });
      login(response.user, response.accessToken, response.refreshToken);
      return { error: null };
    } catch (error) {
      return { error: error instanceof Error ? error : new Error('Login failed') };
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      const response = await authService.register({ email, password });
      login(response.user, response.accessToken, response.refreshToken);
      return { error: null };
    } catch (error) {
      return { error: error instanceof Error ? error : new Error('Registration failed') };
    }
  };

  const signInWithGoogle = async () => {
    try {
      // Redirect to backend Google OAuth endpoint
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
      window.location.href = `${apiBaseUrl}/auth/google`;
      return { error: null };
    } catch (error) {
      return { error: error instanceof Error ? error : new Error('Google sign-in failed') };
    }
  };

  const signOut = async () => {
    try {
      await authService.logout(refreshToken);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      logout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithEmail,
        signUpWithEmail,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
